import React, { useState, useCallback, useMemo, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { DateRangeBox, Tabs } from "devextreme-react";
import { subMonths } from "date-fns";
import { ValueChangedEvent } from "devextreme/ui/date_range_box";
import LocalisationSelector from "@/components/localisation-selector";
import MultiProjectDropdownOptions from "./multi-project-dropdown-options.ts";
import DropdownSelector from "@/components/dropdown-selector/index.tsx";
import { KeyDisplayPair } from "@/models/interfaces/key-value.ts";
import useProjectsIndicatorsContext
    from "@/services/context/indicators-context-provider/use-project-indicators-context.ts";
import SearchBar from "@/components/searchbar";
import useFilteredData from "@/utils/filter-data.ts";
import { Case, Switch } from "@/components/switch";
import "devextreme/dist/css/dx.fluent.blue.light.compact.css"
import useLocationContext from "@/services/context/location/use-location-context.ts";
import useProjectFilterNumberContext
    from "@/services/context/project-filter-number/use-project-filter-number-context.ts";
import GaugesLayout from "./layouts/gauges/gauges-layout.tsx";
import WeeklyDataChart from "./layouts/chart/index.tsx";
import MapComponent from "./layouts/map";
import SimpleLoadingIndicator from "@/components/simple-loading-indicator";
import Button from "@/components/buttons/button";
import CardLayout from "@/layouts/cards";
import useProjectsContext from "@/services/context/projects/use-projects-context.ts";
import ProjectCard from "@/layouts/project-card";
import useProjectOrderByContext from "@/services/context/project-order-by/use-project-order-by-context.ts";
import OrderBy from "@/components/order-by";
import CreateProjectDialog from "@/components/create-project-dialog";


const tabsDataSource = [
    {
        id: 0,
        text: "Quantitatifs",
    },
    {
        id: 1,
        text: "Indicateurs",
    }
];

const now = new Date();
const initMin = subMonths(now, 1);

const MultiProjectsPage: React.FC = () => {
    const { projects, isLoading } = useProjectsContext();

    const { setTypeIndicator } = useProjectsIndicatorsContext();
    const { filteredProjectNumber, setFilteredProjectNumber, } = useProjectFilterNumberContext();

    const { orderBy } = useProjectOrderByContext();

    const [showCreationProjectDialog, setShowCreationProjectDialog] = useState(false);

    const [shownGraphType, setShownGraphType] = useState<number>(0);

    // Etats filtres
    const { selectedLocations } = useLocationContext();
    const [searchQuery, setSearchQuery] = useState('');

    const [selectedOption, setSelectedOption] = useState<KeyDisplayPair<string, string>>({ key: "SHAB", display: "SHAB" });
    const [selectedDateRange, setSelectedDateRange] = useState([initMin, now]);

    // 1. Filtrage par localisation
    const locationFilteredData = useMemo(() => {
        if (!projects || selectedLocations.length === 0) return projects;
        return projects.filter(project =>
            selectedLocations.includes(project.localisation.id)
        );
    }, [projects, selectedLocations]);

    // 2. Filtrage par recherche
    const searchFilteredData = useFilteredData({
        data: locationFilteredData,
        searchQuery,
        searchFields: ['name']
    });

    useEffect(() => {
        let total = 0;
        if (searchFilteredData)
            total = searchFilteredData.length;
        else if (projects)
            total = projects.length;

        setFilteredProjectNumber(total);
    }, [searchFilteredData, setFilteredProjectNumber, projects]);

    // 3. Application du tri (en dernier, car plus gourmand en ressource)
    const sortedData = useMemo(() => {
        if (!searchFilteredData) return [];

        const projectsToSort = [...searchFilteredData];

        switch (orderBy) {
            case "name_asc":
                return projectsToSort.sort((a, b) => a.name.localeCompare(b.name));

            case "name_desc":
                return projectsToSort.sort((a, b) => b.name.localeCompare(a.name));

            case "date_asc":
                return projectsToSort.sort(
                    (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
                );

            case "date_desc":
                return projectsToSort.sort(
                    (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
                );

            default:
                return projectsToSort;
        }
    }, [searchFilteredData, orderBy]);

    const onSelectedDateChange = useCallback((e: ValueChangedEvent) => {
        const value = e.value as [Date | null, Date | null];
        if (value[0] == null || value[1] == null)
            return;

        setSelectedDateRange([value[0], value[1]]);
    }, [setSelectedDateRange]);

    if (isLoading)
        return <SimpleLoadingIndicator text='Chargement des projets' />

    return (
        <>
            <div className='p-3'>
                <div className='flex flex-col mb-4 gap-2'>
                    <div className="flex gap-4">
                        <LocalisationSelector className={"flex gap-4 grow"} />

                        {/* <SortDropdown className="w-1/6"/> */}

                        <SearchBar onSearch={setSearchQuery} />

                        <OrderBy />

                        <Button variant='simple' onClick={() => setShowCreationProjectDialog(true)}
                            data-tooltip-id='create-project-tooltip'>
                            <FontAwesomeIcon icon={faPlus} className='text-green-500' />
                        </Button>
                    </div>
                </div>

                <div className="flex flex-row gap-4 mt-4">
                    <div className="basis-2/3">
                        <CardLayout cards={sortedData} Component={ProjectCard} />

                        {projects &&
                            <div className='fixed bottom-0 right-4 flex-col gap-2'>
                                <div className='relative bottom-2 border bg-white rounded-lg py-1 px-2 shadow-lg'>
                                    <div className="text-xs text-gray-500">
                                        Affichage de {filteredProjectNumber} projets sur {projects.length}
                                    </div>
                                </div>
                            </div>
                        }
                    </div>

                    <div className="basis-1/3 flex flex-col gap-y-4">
                        <Tabs
                            dataSource={tabsDataSource}
                            width="100%"
                            stylingMode={"primary"}
                            selectedIndex={shownGraphType}
                            onSelectionChanged={(e) => {
                                if (e.addedItems.length === 0) return;
                                setShownGraphType(e.addedItems[0].id);
                            }}
                        />

                        <Switch value={shownGraphType}>
                            <Case value={0}>
                                <div className='flex flex-col gap-y-4'>
                                    <div className="flex gap-x-4">
                                        <DropdownSelector
                                            items={MultiProjectDropdownOptions}
                                            selectedItem={selectedOption}
                                            onSelectionChanged={(e) => {
                                                if (e) {
                                                    setSelectedOption(e);
                                                    setTypeIndicator(e.key);
                                                }
                                            }}
                                            keySelector={kd => kd.key}
                                            displaySelector={kd => kd.display}
                                        />

                                        <DateRangeBox
                                            onValueChanged={onSelectedDateChange}
                                            startDate={selectedDateRange[0]}
                                            endDate={selectedDateRange[1]}
                                            max={now}
                                            applyValueMode="useButtons"
                                            labelMode={"hidden"}
                                        />
                                    </div>

                                    <WeeklyDataChart
                                        selectedLocalisation={selectedLocations[0] ?? 0}
                                        selectedOption={selectedOption.key}
                                        selectedDateRange={selectedDateRange}
                                    />

                                    <MapComponent
                                        selectedLocalisation={selectedLocations[0] ?? 0}
                                        selectedOption={selectedOption.key}
                                        selectedDateRange={selectedDateRange}
                                    />
                                </div>
                            </Case>
                            <Case value={1}>
                                <GaugesLayout
                                    localisationId={selectedLocations[0] ?? 0} />
                            </Case>
                            <Case default>
                                <div />
                            </Case>
                        </Switch>

                    </div>
                </div>
            </div>

            <CreateProjectDialog
                open={showCreationProjectDialog} title={'Créer un projet'}
                onClose={() => setShowCreationProjectDialog(false)} />
        </>
    );
};

export default MultiProjectsPage;